import { useEffect, useState, type InputHTMLAttributes } from "react"
import {
  IconButton,
  InputAdornment,
  TextField,
  type TextFieldProps,
} from "@mui/material"
import { Eye, EyeOff } from "lucide-react"

type PasswordInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type"
> & {
  visibilityLabel?: string
}

export const PasswordInput = ({
  className = "",
  visibilityLabel = "password",
  ...props
}: PasswordInputProps) => {
  const [visible, setVisible] = useState(false)

  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? "text" : "password"}
        className={`${className} pr-12`}
      />
      <button
        type="button"
        aria-label={`${visible ? "Hide" : "Show"} ${visibilityLabel}`}
        aria-pressed={visible}
        disabled={props.disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setVisible((current) => !current)}
        className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-md p-2 text-gray-500 hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-50"
      >
        {visible ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  )
}

export const PasswordTextField = (props: TextFieldProps) => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (props.value === "") setVisible(false)
  }, [props.value])

  return (
    <TextField
      {...props}
      type={visible ? "text" : "password"}
      InputProps={{
        ...props.InputProps,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              type="button"
              aria-label={`${visible ? "Hide" : "Show"} ${String(props.label).toLowerCase()}`}
              aria-pressed={visible}
              disabled={props.disabled}
              edge="end"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setVisible((current) => !current)}
            >
              {visible ? <EyeOff size={20} /> : <Eye size={20} />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}
